import { CheckCircle2, XCircle, AlertTriangle, Info } from "lucide-react"
import { Toaster as Sonner, toast } from "sonner"
import { useTheme } from "@/context/ThemeContext"

// Previously stock shadcn defaults — same flat slate-800/border-slate-700
// regardless of success/error/warning, no icon customization anywhere in
// the app (~30+ toast.success/error call sites, none passed one). Read as
// one generic gray "something happened" notice no matter what actually
// happened. Redesigned here, once, so every call site benefits with zero
// changes to any of them: rounded-2xl to match every other surface in the
// app (cards/dialogs/buttons), a real shadow instead of a flat border, and
// a per-type left accent + icon using colors already used everywhere else
// in the app for the same semantics — no new palette, just applying the
// existing one consistently. Same reasoning as index.css's zero-blur
// policy: solid surfaces, no backdrop-filter.
const ICON_CLASS = "w-5 h-5"

// Follows the app theme unless a caller pins one.
const Toaster = ({
  theme,
  ...props
}) => {
  const { theme: appTheme } = useTheme()
  theme = theme || appTheme
  return (
    <Sonner
      theme={theme}
      className="toaster group"
      icons={{
        success: <CheckCircle2 className={`${ICON_CLASS} text-emerald-400`} />,
        error: <XCircle className={`${ICON_CLASS} text-rose-400`} />,
        warning: <AlertTriangle className={`${ICON_CLASS} text-amber-400`} />,
        info: <Info className={`${ICON_CLASS} text-violet-400`} />,
      }}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-slate-900 group-[.toaster]:text-slate-100 group-[.toaster]:border group-[.toaster]:border-white/10 group-[.toaster]:rounded-2xl group-[.toaster]:shadow-xl group-[.toaster]:shadow-black/40 group-[.toaster]:py-3.5 group-[.toaster]:pl-4 group-[.toaster]:pr-4",
          description: "group-[.toast]:text-slate-400",
          actionButton:
            "group-[.toast]:bg-brand group-[.toast]:text-on-brand",
          cancelButton:
            "group-[.toast]:bg-slate-700 group-[.toast]:text-slate-300",
          success: "group-[.toaster]:border-l-4 group-[.toaster]:border-l-emerald-500",
          error: "group-[.toaster]:border-l-4 group-[.toaster]:border-l-rose-500",
          warning: "group-[.toaster]:border-l-4 group-[.toaster]:border-l-amber-500",
          info: "group-[.toaster]:border-l-4 group-[.toaster]:border-l-violet-500",
        },
      }}
      // Phones: below the notch / status bar. Sonner's default 16px top puts
      // toasts under the status bar in the installed iOS app (viewport-fit=cover).
      mobileOffset={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)", left: 12, right: 12 }}
      {...props} />
  );
}

export { Toaster, toast }
